import React, { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import { api } from '../lib/api';

interface Well {
  id: string;
  uwi: string;
  lease: string;
  wellNumber: string;
  status: string;
  wellType: string;
  county: string;
  state: string;
  country: string;
  company: string;
  // coordinates
  latitude: number;
  longitude: number;
  name: string;
  location: string;
  totalPosts: number;
  lastActivity: string;
}

interface AdminWellsProps {
  searchQuery?: string;
  openNotImplementedModal?: (feature: string) => void;
  onViewDetails: (well: Well) => void;
  refreshTrigger?: number;
}
const AdminWells: React.FC<AdminWellsProps> = ({ searchQuery = '', openNotImplementedModal, onViewDetails, refreshTrigger = 0 }) => {
  const [wells, setWells] = useState<Well[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [filters, setFilters] = useState({ q: searchQuery });
  const [sort, setSort] = useState<{ field: string; order: 'asc' | 'desc' }>({ field: 'name', order: 'asc' });

  const fetchWells = async () => {
    const params: any = { page, limit, sort: sort.field, order: sort.order };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const res = await api.wells.getAll(params);
    const wellsData = (res.data.data.wells || []).map((w: any) => ({
      id: w.id,
      uwi: w.uwi,
      lease: w.lease,
      wellNumber: w.well_number ?? w.wellNumber,
      status: w.status,
      wellType: w.well_type ?? w.wellType,
      county: w.county,
      state: w.state,
      country: w.country,
      company: w.company,
      // coordinates
      latitude: w.latitude,
      longitude: w.longitude,
      name: `${w.lease ?? ''} ${w.well_number ?? w.wellNumber ?? ''}`.trim(),
      location: w.location || '',
      totalPosts: w._count?.activities || 0,
      lastActivity: w.updatedAt ? new Date(w.updatedAt).toLocaleDateString() : '',
    }));
    setWells(wellsData);
    setTotal(res.data.data.total);
  };

  useEffect(() => { fetchWells(); }, [page, filters, sort, refreshTrigger]);
  useEffect(() => { setFilters(f => ({ ...f, q: searchQuery })); }, [searchQuery]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6 text-white">
      <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                {['name', 'uwi', 'status', 'county', 'state', 'country', 'company'].map(col => (
                  <th
                    key={col}
                    className="px-6 py-4 text-left text-sm font-medium text-gray-300 cursor-pointer"
                    onClick={() => setSort({ field: col, order: sort.field === col && sort.order === 'asc' ? 'desc' : 'asc' })}
                  >
                    {col.charAt(0).toUpperCase() + col.slice(1)}
                  </th>
                ))}
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {wells.map(well => (
                <tr key={well.id} className="hover:bg-gray-800/50">
                  <td className="px-6 py-4 text-white font-medium">{well.name}</td>
                  <td className="px-6 py-4 text-gray-300">{well.uwi}</td>
                  <td className="px-6 py-4 text-gray-300">{well.status}</td>
                  <td className="px-6 py-4 text-gray-300">{well.county}</td>
                  <td className="px-6 py-4 text-gray-300">{well.state}</td>
                  <td className="px-6 py-4 text-gray-300">{well.country}</td>
                  <td className="px-6 py-4 text-gray-300">{well.company}</td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => onViewDetails(well)}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                      aria-label="View Details"
                    >
                      <Eye className="w-4 h-4 text-gray-400" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Wells Pagination */}
      <div className="flex items-center justify-between mt-6">
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-400">
            Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} wells
          </div>
          {openNotImplementedModal && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => openNotImplementedModal('Import wells')}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                title="Import Wells"
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
              </button>
              <button
                onClick={() => openNotImplementedModal('Export wells')}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                title="Export Wells"
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12M12 16.5V3" />
                </svg>
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPage(p => Math.max(p - 1, 1))}
            disabled={page === 1}
            className="px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button
              key={p}
              onClick={() => setPage(p)}
              className={`px-3 py-2 rounded-lg transition-colors ${
                page === p ? 'bg-white text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => setPage(p => Math.min(p + 1, totalPages))}
            disabled={page === totalPages}
            className="px-3 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

    </div>
  );
};

export default AdminWells;
